import isUndefined from 'lodash/isUndefined';
import omitBy from 'lodash/omitBy';
import { sentryService } from '@/services/telemetry/sentry';
import { isDemographicKey } from '@/shared/lib/demographic-keys';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
import tryParsingJson from '@/shared/utils/try-parsing-json';

export interface RequestContext {
  method?: string;
  url?: string;
  data?: unknown;
  binaries?: Record<string, string | string[]>;
}

export interface ResponseContext {
  status?: { code: number; text: string };
  json?: unknown;
  text?: string | null;
}

export interface ErrorContext {
  id?: string;
  timestamp?: string;
  request?: RequestContext | null;
  response?: ResponseContext | null;
  isRequestManuallyAborted?: boolean;
  statusCode?: number;
  durationMs?: number;
  retriesAttempted?: number;
}

export interface ExtractContextParams {
  response?: Response;
  error?: Error | ParsedError;
  messagePrefix?: string;
  url?: string;
  method?: string;
  data?: unknown;
  binaries?: Record<string, unknown>;
  isRequestManuallyAborted?: boolean;
}

interface ParsedErrorOptions {
  cause?: unknown;
  context?: ErrorContext | null;
  originalStack?: string;
}

export class ParsedError extends Error {
  context: ErrorContext | null;

  constructor(message: string, options: ParsedErrorOptions = {}) {
    super(message, { cause: options.cause });
    this.name = 'ParsedError';
    this.context = options.context ?? null;

    if (options.originalStack) {
      this.stack = options.originalStack;
    }
  }

  // Controls JSON serialization so enormous original errors do not bloat
  // Sentry payloads. `cause` and `stack` are intentionally excluded —
  // Sentry captures stacks natively and `cause` may contain raw SDK objects.
  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      message: this.message?.substring?.(0, 200) || this.message,
      context: this.context,
    };
  }
}

export interface ErrorLogSummary {
  code?: unknown;
  message?: string;
  method?: string;
  name?: string;
  path?: string;
  status?: number;
}

function pathOf(url: unknown): string | undefined {
  if (typeof url !== 'string' || url.length === 0) {
    return undefined;
  }
  try {
    return new URL(url, 'http://kiosk.invalid').pathname;
  } catch {
    return undefined;
  }
}

/**
 * The part of an error that a console log can hold: the method, the path,
 * the HTTP status and the error code. An error of a request carries the
 * request body and the response, and a body can hold the traits of the
 * people. Thus its message, body and response stay out of the log. The
 * message of an error that no request made stays in.
 */
export function errorLogSummary(error: unknown): ErrorLogSummary {
  if (!error || typeof error !== 'object') {
    return { message: String(error) };
  }
  const candidate = error as {
    code?: unknown;
    context?: (ErrorContext & { code?: unknown }) | null;
    message?: unknown;
    name?: unknown;
  };
  const context = candidate.context;
  const isRequestError = Boolean(context?.request || context?.response);
  const responseCode = (context?.response?.json as { code?: unknown } | null)
    ?.code;
  const summary: ErrorLogSummary = {
    name: typeof candidate.name === 'string' ? candidate.name : undefined,
    method: context?.request?.method,
    path: pathOf(context?.request?.url),
    status: context?.response?.status?.code,
    code: candidate.code ?? responseCode ?? context?.code,
  };
  if (!isRequestError && typeof candidate.message === 'string') {
    summary.message = candidate.message;
  }
  return omitBy(summary, isUndefined) as ErrorLogSummary;
}

// Truncation limits keep noisy HTTP bodies bounded. They are not a privacy
// boundary; operational join keys must stay visible for debugging.
const RESPONSE_LENGTH_LIMIT = 500;
const BREADCRUMB_JSON_LIMIT = 500;
const MAX_RECURSION_DEPTH = 5;
const MAX_ARRAY_ITEMS = 10;
const MAX_OBJECT_KEYS = 20;

const SENSITIVE_FIELD_PATTERNS = [
  // Do not include kiosk API keys, session IDs, ticket/download/GoPhoto codes,
  // Stripe payment intent IDs, reader IDs, or device/visitor IDs here. They are
  // internal operational join keys stored in Postgres and needed intact in
  // Sentry to correlate browser, backend, Stripe, and support records.
  // Auth
  'password',
  'passwd',
  'pwd',
  'secret',
  'token',
  'authorization',
  'bearer',
  'credential',
  'access_token',
  'refresh_token',
  'id_token',
  'cookie',
  // Payment
  'credit_card',
  'creditcard',
  'card_number',
  'cardnumber',
  'cvv',
  'cvc',
  'expiry',
  'exp_month',
  'exp_year',
  'account_number',
  'routing_number',
  'iban',
  'swift',
  'client_secret',
  'stripe_secret_key',
  'stripe_signature',
  // Personal
  'ssn',
  'social_security',
  'tax_id',
  'private_key',
  'privatekey',
  'encryption_key',
  'pin',
  'security_code',
  'otp',
  'verification_code',
  // Raw body that might contain anything sensitive
  'raw',
  'rawbody',
  'raw_body',
  'body',
] as const;

const SENSITIVE_URL_PARAMS = [
  'token',
  'secret',
  'password',
  'access_token',
  'refresh_token',
  'id_token',
  'client_secret',
] as const;

const SENSITIVE_URL_PARAM_REGEX = new RegExp(
  `([?&](?:${SENSITIVE_URL_PARAMS.join('|')})=)[^&#\\s]*`,
  'gi'
);

function redactSensitiveUrlParamValues(value: string): string {
  return value.replace(SENSITIVE_URL_PARAM_REGEX, '$1[REDACTED]');
}

// Regex redaction patterns for inline sensitive data.
const REGEX_CREDIT_CARD_SPACED =
  /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{1,7}\b/g;
const REGEX_CREDIT_CARD_RAW = /\b\d{13,19}\b/g;
const REGEX_BEARER_TOKEN = /Bearer\s+[A-Za-z0-9\-_.~+/]+=*/gi;
const REGEX_JWT =
  /eyJ[A-Za-z0-9\-_]+\.eyJ[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_.+/=]*/g;

function sanitizeString(
  str: unknown,
  maxLength = RESPONSE_LENGTH_LIMIT
): string | null {
  if (!str) return null;
  const strValue = typeof str === 'string' ? str : String(str);
  const truncated =
    strValue.length > maxLength
      ? `${strValue.substring(0, maxLength)}...[truncated]`
      : strValue;
  const sanitized = truncated
    .replace(REGEX_CREDIT_CARD_SPACED, '[CARD_REDACTED]')
    .replace(REGEX_CREDIT_CARD_RAW, '[CARD_REDACTED]')
    .replace(REGEX_BEARER_TOKEN, 'Bearer [TOKEN_REDACTED]')
    .replace(REGEX_JWT, '[JWT_REDACTED]');
  return redactSensitiveUrlParamValues(sanitized);
}

function sanitizeObject(
  obj: unknown,
  depth = 0,
  seen: WeakSet<object> = new WeakSet()
): unknown {
  if (obj === null || obj === undefined) return null;
  if (depth > MAX_RECURSION_DEPTH) return '[MAX_DEPTH_EXCEEDED]';
  if (typeof obj === 'string') return sanitizeString(obj);
  if (typeof obj === 'number' || typeof obj === 'boolean') return obj;

  if (typeof obj === 'object') {
    if (seen.has(obj)) return '[CIRCULAR_REFERENCE]';
    seen.add(obj);
  }

  if (Array.isArray(obj)) {
    const sanitized: unknown[] = obj
      .slice(0, MAX_ARRAY_ITEMS)
      .map((item) => sanitizeObject(item, depth + 1, seen));
    if (obj.length > MAX_ARRAY_ITEMS) {
      sanitized.push(`[...${obj.length - MAX_ARRAY_ITEMS} more items]`);
    }
    return sanitized;
  }

  if (typeof obj === 'object') {
    const record = obj as Record<string, unknown>;
    const sanitized: Record<string, unknown> = {};
    let keys: string[];
    try {
      keys = Object.keys(record);
    } catch {
      return '[UNREADABLE_OBJECT]';
    }

    for (let i = 0; i < Math.min(keys.length, MAX_OBJECT_KEYS); i++) {
      const key = keys[i]!;
      const lowerKey = key.toLowerCase();
      // Keep operational join keys visible in Sentry. Broad fragments like
      // "key", "code", "id", "session", and "pass" are not credential
      // classes here: they mask kiosk API keys, PhotoPass/GoPhoto codes,
      // ticket/download codes, payment/reader IDs, visitor IDs, and image UUIDs
      // that are stored in Postgres and required for incident correlation.
      const isSensitive = SENSITIVE_FIELD_PATTERNS.some((p) =>
        lowerKey.includes(p)
      );
      // The error context goes to the console and to Sentry. A request body
      // holds the traits of the people (a generation, a group portrait), and
      // their values never go there.
      if (isDemographicKey(key)) {
        sanitized[key] = '[FILTERED]';
        continue;
      }
      sanitized[key] = isSensitive
        ? '[REDACTED]'
        : sanitizeObject(record[key], depth + 1, seen);
    }

    if (keys.length > MAX_OBJECT_KEYS) {
      sanitized.__truncated__ = `${keys.length - MAX_OBJECT_KEYS} more keys omitted`;
    }
    return sanitized;
  }

  return '[UNSUPPORTED_TYPE]';
}

function sanitizeUrl(url: unknown): string | undefined {
  if (!url) return undefined;

  try {
    const urlObj = new URL(String(url), window.location.origin);
    for (const param of SENSITIVE_URL_PARAMS) {
      if (urlObj.searchParams.has(param)) {
        urlObj.searchParams.set(param, '[REDACTED]');
      }
    }
    return urlObj.toString();
  } catch {
    // URL parsing failed; preserve operational query metadata where possible
    // and redact only credential params. Query-wide stripping hides the exact
    // kiosk api_key/key, QR/ticket/session IDs, visitor IDs, and image UUIDs
    // operators need to join Sentry with backend/Postgres records.
    const str = typeof url === 'string' ? url : String(url);
    const sanitized = redactSensitiveUrlParamValues(str);
    return sanitized.length > 200
      ? `${sanitized.substring(0, 200)}...[truncated]`
      : sanitized;
  }
}

function sanitizeResponseJson(json: unknown): string | null {
  if (!json) return null;
  try {
    const str = JSON.stringify(json);
    const escapedPatterns = SENSITIVE_FIELD_PATTERNS.map((p) =>
      p.replace(/[.*+?^${}()|[\]-]/g, '\\$&')
    ).join('|');

    const sensitiveFieldRegex = new RegExp(
      `("[^"]*(?:${escapedPatterns})[^"]*"\\s*:\\s*)"[^"]*"`,
      'gi'
    );
    const sensitiveNumericRegex = new RegExp(
      `("[^"]*(?:${escapedPatterns})[^"]*"\\s*:\\s*)\\d+`,
      'gi'
    );

    return str
      .slice(0, BREADCRUMB_JSON_LIMIT)
      .replace(sensitiveFieldRegex, '$1"[REDACTED]"')
      .replace(sensitiveNumericRegex, '$1[REDACTED]')
      .replace(REGEX_JWT, '[JWT_REDACTED]')
      .replace(REGEX_CREDIT_CARD_SPACED, '[CARD_REDACTED]')
      .replace(REGEX_CREDIT_CARD_RAW, '[CARD_REDACTED]');
  } catch {
    return '[JSON_STRINGIFY_FAILED]';
  }
}

async function extractHttpResponseContext(
  response?: Response
): Promise<ResponseContext> {
  if (!response) return {};
  const status = { code: response.status, text: response.statusText };
  let json: unknown = null;
  let text: string | null = null;

  try {
    const responseRaw = await response.text();
    const parsedJson = tryParsingJson(responseRaw, 'api_response');
    if (parsedJson) {
      json = sanitizeObject(parsedJson);
    } else {
      text = sanitizeString(responseRaw, RESPONSE_LENGTH_LIMIT);
    }
  } catch {
    text = '[RESPONSE_BODY_UNREADABLE]';
  }

  return { status, json, text };
}

function extractHttpRequestContext(
  params: ExtractContextParams
): RequestContext {
  const rawUrl = params.url ?? params.response?.url;
  const method = params.method;
  const url = sanitizeUrl(rawUrl);
  const data = params.data ? sanitizeObject(params.data) : undefined;

  let binaries: Record<string, string | string[]> | undefined;
  const rawBinaries =
    params.binaries && typeof params.binaries === 'object'
      ? params.binaries
      : null;
  if (rawBinaries && Object.keys(rawBinaries).length > 0) {
    binaries = {};
    for (const [key, binary] of Object.entries(rawBinaries)) {
      if (binary == null) {
        binaries[key] = '[null]';
      } else if (Array.isArray(binary)) {
        binaries[key] = binary.map(
          (item: { name?: string } | null | undefined) =>
            item?.name ?? '[unnamed]'
        );
      } else {
        binaries[key] = (binary as { name?: string }).name ?? '[unnamed]';
      }
    }
  }

  return { method, url, data, binaries };
}

function generateErrorMetaInfo(): { id: string; timestamp: string } {
  return {
    id: Math.round(Math.random() * 10000).toString(),
    timestamp: new Date().toISOString(),
  };
}

function generateErrorMessage(
  error: Error | ParsedError | undefined,
  httpResponseContext: ResponseContext
): string {
  if (error) {
    let errorStr: string | null = null;
    try {
      errorStr = error?.toString?.() ?? null;
    } catch {
      errorStr = null;
    }
    if (errorStr === '[object Object]') errorStr = null;
    const rawMessage =
      error?.message || errorStr || 'Unknown error (cannot extract error data)';
    return typeof rawMessage === 'string' &&
      rawMessage.length > RESPONSE_LENGTH_LIMIT
      ? `${rawMessage.substring(0, RESPONSE_LENGTH_LIMIT)}...[truncated]`
      : rawMessage;
  }

  const { status, json, text } = httpResponseContext;
  const errorMsg = (json as { error?: unknown } | null)?.error;
  const messageMsg = (json as { message?: unknown } | null)?.message;

  // Detect HTML proxy error pages (502/503/504 from nginx/CloudFlare/Caddy)
  // and replace with a descriptive message instead of raw HTML. Match
  // either `<!doctype html>` (FastAPI debug pages) or `<html>` (host nginx
  // 502 default page) — the old regex only caught the former, so every
  // nginx 502 surfaced with a unique HTML body as the Sentry title,
  // producing ~5 distinct issues per endpoint that should have grouped
  // into one. KIOSK-FRONTEND-KE / D4 / NB / M0 / P2 (~13k events combined).
  const isHtmlResponse =
    typeof text === 'string' && /^\s*(?:<!doctype\b|<html\b)/i.test(text);
  const cleanText = isHtmlResponse
    ? `${status?.code ?? 'HTTP'} ${status?.text ?? 'proxy error'}`
    : text;

  let message =
    (typeof errorMsg === 'string' ? errorMsg : null) ||
    // Handle nested pattern: { error: { message: "..." } }
    (typeof errorMsg === 'object' &&
    typeof (errorMsg as { message?: unknown })?.message === 'string'
      ? (errorMsg as { message: string }).message
      : null) ||
    (typeof messageMsg === 'string' ? messageMsg : null) ||
    cleanText ||
    (status ? [status.code, status.text].join(' ') : 'Unknown error');

  if (typeof message === 'string' && message.length > RESPONSE_LENGTH_LIMIT) {
    message = `${message.substring(0, RESPONSE_LENGTH_LIMIT)}...[truncated]`;
  }
  return message;
}

function generateErrorContext(
  error: Error | ParsedError | undefined,
  httpRequestContext: RequestContext,
  httpResponseContext: ResponseContext,
  isRequestManuallyAborted?: boolean
): ErrorContext {
  const errorMeta = generateErrorMetaInfo();
  const existingContext = (error as ParsedError | undefined)?.context ?? {};

  const id = existingContext.id ?? errorMeta.id;
  const timestamp = existingContext.timestamp ?? errorMeta.timestamp;
  const request =
    (omitBy(
      { ...existingContext.request, ...httpRequestContext },
      isUndefined
    ) as RequestContext) || null;
  const response =
    (omitBy(
      { ...existingContext.response, ...httpResponseContext },
      isUndefined
    ) as ResponseContext) || null;

  const context: ErrorContext = { id, timestamp, request, response };
  if (isRequestManuallyAborted) {
    context.isRequestManuallyAborted = true;
  }
  return context;
}

function generateStack(
  error: Error | undefined,
  message: string
): string | undefined {
  // Prioritize original error's stack to preserve actual error location.
  return error?.stack ?? new Error(message).stack;
}

function isSufficientContextProvided(params: ExtractContextParams): boolean {
  return Boolean(params?.response || params?.error);
}

async function extractContext(
  params: ExtractContextParams
): Promise<
  | { message: string; stack?: string; context: ErrorContext }
  | Record<string, never>
> {
  if (!isSufficientContextProvided(params)) return {};

  const httpRequestContext = extractHttpRequestContext(params);
  const httpResponseContext = await extractHttpResponseContext(params.response);
  const errorMessage = generateErrorMessage(params.error, httpResponseContext);

  const message = params.messagePrefix
    ? `${params.messagePrefix}: ${errorMessage}`
    : errorMessage;
  const context = generateErrorContext(
    params.error,
    httpRequestContext,
    httpResponseContext,
    params.isRequestManuallyAborted
  );
  const stack = generateStack(params.error, message);

  return { message, stack, context };
}

// biome-ignore lint/complexity/noStaticOnlyClass: namespace grouping for backward compat with old ErrorParser API
export class ErrorParser {
  static _sanitizeObject = sanitizeObject;
  static _sanitizeUrl = sanitizeUrl;
  static _sanitizeResponseJson = sanitizeResponseJson;
  static generateErrorMessage = generateErrorMessage;

  // CRITICAL: the error parser itself must NEVER throw. If context extraction
  // fails, fall back to a minimal error so callers always get a ParsedError.
  static async generateErrorWithContext(
    params: ExtractContextParams
  ): Promise<ParsedError> {
    const originalError = params?.error as Error | undefined;
    let message: string;
    let context: ErrorContext | null;
    let stack: string | undefined;

    try {
      const extracted = await extractContext(params);
      if ('message' in extracted) {
        message = extracted.message;
        context = extracted.context;
        stack = extracted.stack;
      } else {
        message = originalError?.message ?? 'Unknown error';
        context = null;
        stack = originalError?.stack;
      }
    } catch (extractionError) {
      console.warn(
        '[ErrorParser] Context extraction failed:',
        (extractionError as Error)?.message
      );
      message =
        originalError?.message ?? 'Unknown error (context extraction failed)';
      context = null;
      stack = originalError?.stack ?? new Error(message).stack;
    }

    const sanitizedOriginalMessage = originalError?.message
      ? sanitizeString(originalError.message, RESPONSE_LENGTH_LIMIT)
      : undefined;
    sentryService.addBreadcrumb(
      'error.parsed',
      sanitizeString(message, RESPONSE_LENGTH_LIMIT) ?? message,
      {
        errorId: context?.id,
        timestamp: context?.timestamp,
        url: context?.request?.url,
        method: context?.request?.method,
        statusCode: context?.response?.status?.code,
        statusText: context?.response?.status?.text,
        responseJson: sanitizeResponseJson(context?.response?.json),
        responseText:
          context?.response?.text?.substring?.(0, RESPONSE_LENGTH_LIMIT) ??
          null,
        isRequestManuallyAborted: context?.isRequestManuallyAborted,
        originalErrorName: originalError?.name,
        originalErrorMessage: sanitizedOriginalMessage,
      },
      'error'
    );

    return new ParsedError(message, {
      cause: originalError,
      context,
      originalStack: stack,
    });
  }
}

exposeToGlobalScope(ErrorParser, 'ErrorParser');
exposeToGlobalScope(ParsedError, 'ParsedError');
